// O REPASSE AOS CREDORES (#279), igual nas duas telas: a da averbadora (no
// resultado da consignataria) e a do orgao (na competencia). Quem usa passa as
// tres operacoes do SDK da sua porta — listar, registrar, url do comprovante —
// e se a pessoa pode registrar. Aqui ficam a lista, os totais e o formulario.

import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, DataTable, EstadoVazio, Modal, SelectField, TextField, TextareaField, fmtBRL, type Column } from "@atlas/ui/web";
import type { PedidoDeRepasse, Repasse, TotaisConciliacao } from "@atlas/sdk";
import { mensagemDeErro } from "../lib/erro-validacao";
import { downloadAuthed } from "../routes/prefeitura/_ui";

const reais = (c: number) => fmtBRL(c / 100);

/** "120,50" → 12050. Vazio, zero ou inválido → null. */
export function centavosDoTexto(texto: string): number | null {
  const limpo = texto.trim().replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(limpo)) return null;
  const c = Math.round(Number(limpo) * 100);
  return c > 0 ? c : null;
}

/** Os três totais e o saldo. Positivo = falta repassar; negativo = repasse a maior. */
export function TotaisDaConciliacao({ totais }: { totais: TotaisConciliacao }) {
  const saldo = totais.saldoARepassarCentavos;
  const itens = [
    { rotulo: "Pedido", valor: reais(totais.pedidoCentavos), dica: "O que a consignatária informou no arquivo do mês." },
    { rotulo: "Descontado", valor: reais(totais.descontadoCentavos), dica: "O que a folha pagou, desconto parcial incluso." },
    { rotulo: "Repassado", valor: reais(totais.repassadoCentavos), dica: "O que o órgão registrou como transferido ao credor." },
    {
      rotulo: saldo < 0 ? "Repasse a maior" : "Saldo a repassar",
      valor: reais(Math.abs(saldo)),
      dica: saldo < 0 ? "O órgão repassou mais do que a folha descontou." : "Descontado e ainda não repassado.",
      cor: saldo > 0 ? "var(--danger-500)" : saldo < 0 ? "var(--gold-500)" : undefined,
    },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
      {itens.map((i) => (
        <div key={i.rotulo} title={i.dica} style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-dim)" }}>{i.rotulo}</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: i.cor }}>{i.valor}</span>
        </div>
      ))}
    </div>
  );
}

export interface PortaDoRepasse {
  /** Parte fixa da queryKey: quem invalida depois do registro. */
  chave: readonly unknown[];
  listar: () => Promise<{ repasses: Repasse[] }>;
  registrar: (p: PedidoDeRepasse) => Promise<{ repasse: Repasse }>;
  comprovanteUrl: (id: number) => string;
}

export function RepassesPainel({
  titulo,
  porta,
  credores,
  podeRegistrar,
  totais,
  aoRegistrar,
}: {
  titulo: string;
  porta: PortaDoRepasse;
  /** Os credores que o formulário oferece. Um só = já escolhido. */
  credores: { value: string; label: string }[];
  podeRegistrar: boolean;
  totais?: TotaisConciliacao;
  aoRegistrar?: () => void;
}) {
  const [registrando, setRegistrando] = useState(false);
  const q = useQuery({ queryKey: [...porta.chave, "repasses"], queryFn: porta.listar });
  const nomeDo = (credorId: number) => credores.find((c) => c.value === String(credorId))?.label ?? `Credor ${credorId}`;
  const colunas: Column<Repasse>[] = [
    { key: "data", header: "Data", mono: true, render: (r) => r.dataRepasse.split("-").reverse().join("/") },
    { key: "credor", header: "Credor", render: (r) => nomeDo(r.credorId) },
    { key: "valor", header: "Valor", render: (r) => reais(r.valorCentavos) },
    { key: "observacao", header: "Observação", wrap: true, render: (r) => r.observacao ?? "—" },
    {
      key: "comprovante",
      header: "Comprovante",
      render: (r) =>
        r.temComprovante ? (
          <Button size="sm" variant="ghost" onClick={() => void downloadAuthed(porta.comprovanteUrl(r.id), r.comprovanteNome ?? "comprovante")}>
            Baixar
          </Button>
        ) : (
          "—"
        ),
    },
  ];
  return (
    <Card>
      <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>{titulo}</h2>
        {podeRegistrar ? <Button onClick={() => setRegistrando(true)}>Registrar repasse</Button> : null}
      </div>
      {totais ? <div style={{ margin: "12px 0" }}><TotaisDaConciliacao totais={totais} /></div> : null}
      {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar os repasses.")}</p>}
      <DataTable
        caption="Repasses registrados"
        columns={colunas}
        rows={q.data?.repasses ?? []}
        rowKey={(r) => String(r.id)}
        loading={q.isLoading}
        emptyState={<EstadoVazio titulo="Nenhum repasse registrado nesta competência." />}
      />
      {registrando ? (
        <RegistrarRepasseModal
          porta={porta}
          credores={credores}
          onFechar={() => setRegistrando(false)}
          aoRegistrar={aoRegistrar}
        />
      ) : null}
    </Card>
  );
}

function RegistrarRepasseModal({
  porta,
  credores,
  onFechar,
  aoRegistrar,
}: {
  porta: PortaDoRepasse;
  credores: { value: string; label: string }[];
  onFechar: () => void;
  aoRegistrar?: () => void;
}) {
  const qc = useQueryClient();
  const [credor, setCredor] = useState(credores.length === 1 ? credores[0]!.value : "");
  const [valor, setValor] = useState("");
  const [data, setData] = useState("");
  const [observacao, setObservacao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const centavos = centavosDoTexto(valor);
  const valido = credor !== "" && centavos !== null && /^\d{4}-\d{2}-\d{2}$/.test(data);
  const salvar = useMutation({
    mutationFn: () =>
      porta.registrar({
        credorId: Number(credor),
        valorCentavos: centavos!,
        dataRepasse: data,
        ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
        ...(arquivo ? { comprovante: arquivo } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...porta.chave] });
      aoRegistrar?.();
      onFechar();
    },
  });
  return (
    <Modal
      open
      title="Registrar repasse"
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Registrando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        <SelectField
          label="Credor"
          value={credor}
          onChange={(e) => setCredor(e.target.value)}
          options={[{ value: "", label: "Escolha o credor" }, ...credores]}
        />
        <TextField label="Valor repassado (R$)" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="1.200,00" />
        <TextField label="Data do repasse" type="date" value={data} onChange={(e) => setData(e.target.value)} />
        <TextareaField label="Observação" value={observacao} onChange={(e) => setObservacao(e.target.value)} hint="Opcional: número da TED, do documento, parcela." />
        <label style={{ display: "grid", gap: 4 }}>
          <span>Comprovante (PDF ou imagem, até 5 MB)</span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            aria-label="Comprovante do repasse"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setArquivo(e.target.files?.[0] ?? null)}
          />
        </label>
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível registrar o repasse.")}</p>}
      </div>
    </Modal>
  );
}
